/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { useState } from 'react'
import Image from 'next/image'
import LegacyImage from 'next/legacy/image'
import { useTranslations } from 'next-intl'
import datoURLToAssetSubdir from 'lib/formatting/dato-url-to-asset-subdir'
import { Products } from '@web/product-meta'
import { FlightIcon, MDSButton } from '@hashicorp/mds-react/components'
import VideoPlayerModal from 'components/video-player-modal'
import s from './style.module.css'

interface CaseStudyHeaderProps {
	brand: string
	customerName: string
	customerLogo: { url: string; alt: string; width: number; height: number }
	partnerLogo?: { url: string; alt: string; width: number; height: number }
	products: Array<Products>
	heading: string
	description: string
	heroImage: CaseStudyHeroImageProps
	wistiaUrl?: string
	videoHeadline?: string
	hasPaddingBottom?: boolean
	caseStudyPdf?: {
		url: string
	}
}

interface CaseStudyHeroImageProps {
	alt: string
	height: number
	responsiveImage: {
		base64: string
	}
	url: string
	width: number
}

export default function CaseStudyHeader({
	customerName,
	customerLogo,
	partnerLogo,
	products,
	heading,
	description,
	heroImage,
	wistiaUrl,
	videoHeadline,
	hasPaddingBottom = false,
	caseStudyPdf,
}: CaseStudyHeaderProps) {
	const [isOpen, setIsOpen] = useState(false)
	const onDismiss = () => {
		setIsOpen(!isOpen)
	}
	const isVideo = wistiaUrl && videoHeadline
	const headerBaseClasses = s.caseStudyHeader + ' case-study-header-legacy'
	const headerClasses = hasPaddingBottom
		? `${headerBaseClasses} ${s.hasPaddingBottom}`
		: headerBaseClasses
	const backgroundImgClass = isVideo ? s.backgroundImgPoster : s.backgroundImg
	const imgLayout = isVideo ? 'fill' : null

	return (
		<header className={headerClasses}>
			<div className={`${s.innerHeader} g-grid-container`}>
				<ProductsUsed products={products} />
				<Headline
					customerLogo={customerLogo}
					customerName={customerName}
					partnerLogo={partnerLogo}
					description={description}
					heading={heading}
					caseStudyPdf={caseStudyPdf}
				/>
			</div>
			<div className={s.heroMediaWrapper}>
				<div className={s.heroMedia}>
					<div className={`${backgroundImgClass} hero-image-legacy`}>
						<LegacyImage
							src={heroImage.url}
							width={heroImage.width}
							height={heroImage.height}
							layout={imgLayout}
							objectFit="cover"
							alt={heroImage.alt}
							placeholder="blur"
							blurDataURL={heroImage.responsiveImage.base64}
							priority={true}
						/>
					</div>
					{isVideo ? (
						<button
							className={s.videoTriggerBttn}
							onClick={onDismiss}
							aria-label={'Play video'}
						>
							<span className={s.iconWrapper}>
								<PlayButtonIcon />
							</span>
							<h2 className={s.videoHeadline}>{videoHeadline}</h2>
						</button>
					) : null}
					{isVideo && isOpen ? (
						<VideoPlayerModal
							onDismiss={onDismiss}
							videoUrl={wistiaUrl}
							label={customerName}
						/>
					) : null}
				</div>
			</div>
		</header>
	)
}

const PlayButtonIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="48"
		height="48"
		viewBox="0 0 72 72"
		fill="none"
	>
		<rect width="72" height="72" rx="36" fill="#E9EAEC" />
		<path
			d="M47 34.7012C48 35.2785 48 36.7219 47 37.2992L30.5 46.8255C29.5 47.4029 28.25 46.6812 28.25 45.5265V26.4739C28.25 25.3192 29.5 24.5975 30.5 25.1749L47 34.7012Z"
			fill="#282A2F"
		/>
	</svg>
)

function ProductsUsed({ products }) {
	const t = useTranslations('CaseStudies')
	const productUsedText = t('hashiCorpProductsUsed')

	return (
		<div className={s.productsUsed}>
			<p className={`${s.productUsedText} g-type-label`}>{productUsedText}</p>
			<div className={s.productsUsedLogos}>
				{products.map((product) => (
					<div key={product}>
						<FlightIcon
							name={`${product}-color`}
							size={24}
							style={{ width: 32, height: 32 }}
						/>
					</div>
				))}
			</div>
		</div>
	)
}

function Headline({
	customerLogo,
	customerName,
	partnerLogo,
	description,
	heading,
	caseStudyPdf,
}: {
	customerLogo: { url: string; alt: string; width: number; height: number }
	customerName: string
	partnerLogo?: { url: string; alt: string; width: number; height: number }
	description?: string
	heading: string
	caseStudyPdf?: { url: string }
}) {
	const t = useTranslations('CaseStudies')
	const logoTitleText = t('customerStory')

	return (
		<div className={s.headlineWrapper}>
			<p className={s.logoTitle}>
				<span className={s.logoTitleCustomer}>
					<Image
						className={`${s.logoTitleImg} logo-title-img-legacy`}
						src={customerLogo.url}
						width={customerLogo.width}
						height={customerLogo.height}
						alt={customerName}
					/>
				</span>
				{partnerLogo ? (
					<span className={s.logoTitlePartner}>
						<Image
							className={`${s.logoTitleImg} logo-title-img-legacy`}
							src={partnerLogo.url}
							width={partnerLogo.width}
							height={partnerLogo.height}
							alt={partnerLogo.alt}
						/>
					</span>
				) : null}
				<span className={`${s.logoTitleSpan} g-type-label`}>
					{logoTitleText}
				</span>
			</p>
			<div className={s.headlineTitleContainer}>
				<h1
					className={`${s.headlineTitle} g-type-display-1 headline-title-legacy`}
				>
					{/* Injecting HTML here because:

					The original field type in DatoCMS was single line string. We now require line breaks and in order to maintain the current entries with minimal code changes, we can only convert the field to multi-line text.
					
					The structured text field type would be preferred if we were building this page from scratch or had only a few live records.
					
					An attempt was made to use `word-break: keep-all;`, however that would also require edits to every Japanese and Korean case study. See https://github.com/hashicorp/web/pull/2289/files#r1962464735 for more info.
					*/}
					<div dangerouslySetInnerHTML={{ __html: heading }} />
				</h1>
				{description && (
					<p className={`${s.headlineDescription} g-type-body-large`}>
						{description}
					</p>
				)}
				{caseStudyPdf ? (
					<MDSButton
						title={t('downloadCustomerStory')}
						url={datoURLToAssetSubdir(caseStudyPdf.url)}
					/>
				) : null}
			</div>
		</div>
	)
}
